import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { findByPincode } from "../lib/pincodes";
import { useLiveSearch } from "../lib/geocode";

// CRA's bundler breaks Leaflet's default marker icon URL resolution unless
// reset like this — a well-known Leaflet+webpack fix, not project-specific.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

/* Tap-anywhere-on-the-map location picker. Uses OpenStreetMap tiles (free,
   no API key/account needed) via Leaflet. This is the one part of the
   location feature that does load something from an external server (map
   tile images) rather than being fully bundled/offline like the city list
   and PIN code lookup — flagged deliberately since everything else in this
   app avoids external dependencies. No search terms or personal data are
   sent, just "give me the map tiles for this area." */
export default function MapPicker({ initialLat, initialLon, onPick, onClose, t }) {
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [picked, setPicked] = useState(
    initialLat != null && initialLon != null ? { lat: initialLat, lon: initialLon } : null
  );
  const [searchText, setSearchText] = useState("");
  const { results: liveResults, loading: searching } = useLiveSearch(searchText);

  // PIN code stays an instant, offline exact lookup (no need to wait on a
  // live request for it); free-text search now goes through Nominatim
  // (see lib/geocode.js) so any place name works, not just the ~200-entry
  // bundled list. Picking a result pans/zooms the map there — the user can
  // still tap to fine-tune the exact spot afterward.
  const pinHit = /^\d{6}$/.test(searchText.trim()) ? findByPincode(searchText.trim()) : null;
  const searchResults = [
    ...(pinHit ? [{ label: pinHit.name, lat: pinHit.lat, lon: pinHit.lon }] : []),
    ...liveResults,
  ];

  const flyToResult = (result) => {
    const map = mapRef.current;
    if (!map) return;
    if (markerRef.current) map.removeLayer(markerRef.current);
    markerRef.current = L.marker([result.lat, result.lon]).addTo(map);
    map.flyTo([result.lat, result.lon], 13);
    setPicked({ lat: result.lat, lon: result.lon });
    setSearchText(result.label);
    setSearchResults([]);
  };

  useEffect(() => {
    const startLat = initialLat != null ? initialLat : 20.5937;
    const startLon = initialLon != null ? initialLon : 78.9629;
    const map = L.map(mapElRef.current).setView([startLat, startLon], initialLat != null ? 11 : 5);
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 18,
    }).addTo(map);

    if (initialLat != null && initialLon != null) {
      markerRef.current = L.marker([initialLat, initialLon]).addTo(map);
    }

    map.on("click", (e) => {
      const { lat, lng } = e.latlng;
      if (markerRef.current) map.removeLayer(markerRef.current);
      markerRef.current = L.marker([lat, lng]).addTo(map);
      setPicked({ lat, lon: lng });
    });

    // Leaflet needs a nudge to measure its container correctly once it's
    // actually visible (it was 0x0 at construction time inside a modal).
    setTimeout(() => map.invalidateSize(), 100);

    return () => map.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="map-picker-overlay" onClick={onClose}>
      <div className="map-picker-box" onClick={(e) => e.stopPropagation()}>
        <div className="map-picker-header">
          <span>{t("mapPickerTitle")}</span>
          <button type="button" className="map-picker-close" onClick={onClose}>&times;</button>
        </div>
        <div className="map-picker-search-wrap">
          <input
            type="text"
            className="map-picker-search-input"
            placeholder={t("mapPickerSearchPlaceholder")}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searching && <div className="map-picker-search-empty">{t("mapPickerSearching")}</div>}
          {!searching && searchResults.length > 0 && (
            <ul className="map-picker-search-results">
              {searchResults.map((r, i) => (
                <li key={`${r.label}-${i}`} onClick={() => flyToResult(r)}>
                  {r.label}
                </li>
              ))}
            </ul>
          )}
          {!searching && searchText.trim().length >= 3 && searchResults.length === 0 && (
            <div className="map-picker-search-empty">{t("mapPickerSearchNoResults")}</div>
          )}
        </div>
        <div ref={mapElRef} className="map-picker-map" />
        <div className="map-picker-footer">
          <span className="map-picker-coords">
            {picked ? `${picked.lat.toFixed(4)}, ${picked.lon.toFixed(4)}` : t("mapPickerHint")}
          </span>
          <button
            type="button"
            className="dharma-send map-picker-confirm"
            disabled={!picked}
            onClick={() => picked && onPick(picked)}
          >
            {t("mapPickerConfirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
